import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getSession } from '../lib/auth.js';
import { setSentryUser } from '../lib/sentry.js';

/**
 * AuthGate — redirects unauthenticated users out of protected routes.
 *
 * Routing behavior:
 *   - Public visitors hitting "/" (the portal root) get sent to
 *     /coming-soon. Most external traffic lands at "/" so this is the
 *     entry point — it should feel like a brand landing page, not an
 *     auth wall.
 *   - Public visitors hitting any OTHER protected route (deep link,
 *     bookmark, etc.) get sent to /login so they can sign in directly.
 *   - Authenticated users render the child route unchanged.
 *
 * Side effect: attaches the Cognito sub (UUID only, no PII) to the
 * Sentry scope so crashes can be correlated to a session.
 */
export default function AuthGate({ children }) {
  const authed = isAuthenticated();
  const location = useLocation();
  useEffect(() => {
    if (!authed) { setSentryUser(null); return; }
    const session = getSession?.();
    // Mock session has `id`; Cognito session will have `sub`. Either is OK
    // — both are opaque UUIDs, no PII.
    const id = session?.sub || session?.id || null;
    setSentryUser(id);
  }, [authed]);
  if (!authed) {
    // Root → Coming Soon (brand landing). Everything else → Login.
    const target = location.pathname === '/' ? '/coming-soon' : '/login';
    return <Navigate to={target} replace />;
  }
  return children;
}
